// src/components/Steps/DisconnectCharger/DisconnectCharger.jsx
import React, { useEffect, useRef, useState } from 'react';
import { StatusView, StatusScreen } from '../../../components';
import { STATUS_KEYS as STATUS } from '../../../constants/statusKeys';
import { useSession } from '../../../context/SessionContext';
import { getCustomerSession, forceEndSession } from '../../../api/sessionsApi';
import { clearActiveSession } from '../../../utils/sessionPersistence';
import { translateStopError } from '../../../utils/stopSessionErrorMessages';
import './DisconnectCharger.less';

// Bounded finalization poll. 30 attempts × 1s ≈ 30s of grace time
// mirrors ChargeMonitoring's Part 6 contract so the two screens agree
// on how long "we're waiting" is acceptable. After that, we surface a
// fallback state instead of pinning the user on Unplug indefinitely.
const FINALIZATION_POLL_INTERVAL_MS = 1000;
const FINALIZATION_POLL_MAX_ATTEMPTS = 30;

// Backend statuses that authoritatively mean "the session is done and
// finalization can be trusted downstream". Matches the values already
// treated as terminal in ChargeMonitoring's finalization poll.
const BACKEND_TERMINAL_STATUSES = new Set([
    'completed',
    'finalized',
    'stopped',
]);

function isBackendFinalized(session) {
    if (!session) return false;
    if (session.finalized_at != null) return true;
    if (session.is_finalized === true) return true;
    if (typeof session.status === 'string'
        && BACKEND_TERMINAL_STATUSES.has(session.status.toLowerCase())) return true;
    return false;
}

const DisconnectCharger = ({ handleIncreaseStep }) => {
    const { state } = useSession();
    const { stationId, live, debugMode, sessionUid } = state;

    const previousStatusRef = useRef(null);
    const hasNavigatedRef = useRef(false);
    const [fallbackVisible, setFallbackVisible] = useState(false);
    // Bumped by the Force End handler to re-arm the terminal-status
    // poll after it has already timed out. See handleForceEnd below.
    const [pollGeneration, setPollGeneration] = useState(0);

    // Use ref to store handleIncreaseStep to avoid dependency issues
    const handleIncreaseStepRef = useRef(handleIncreaseStep);
    handleIncreaseStepRef.current = handleIncreaseStep;

    // Reset the "navigated" latch when the session changes (defensive:
    // a rehydration onto DISCONNECT_CHARGER for a different session must
    // not inherit the previous session's navigation lock).
    useEffect(() => {
        hasNavigatedRef.current = false;
        setFallbackVisible(false);
    }, [sessionUid]);

    // Fast path: the OCPP connector reports Available. Real chargers
    // send StatusNotification → Available after cable removal, so this
    // is the "instant advance on physical unplug" behaviour.
    useEffect(() => {
        if (!stationId) return;

        const currentStatus = live?.status;
        const previousStatus = previousStatusRef.current;

        if (currentStatus !== previousStatus) {
            console.log('[DisconnectCharger] status change:', {
                from: previousStatus,
                to: currentStatus,
                stationId,
                timestamp: new Date().toISOString(),
                meterValue: live?.meter_value,
                energyKWh: live?.energyKWh,
            });
        }

        if (
            currentStatus === 'Available'
            && previousStatus !== 'Available'
            && !hasNavigatedRef.current
        ) {
            console.log('[DisconnectCharger] connector Available — advancing to summary');
            if (!debugMode) {
                hasNavigatedRef.current = true;
                setTimeout(() => {
                    handleIncreaseStepRef.current();
                }, 500);
            }
        }

        previousStatusRef.current = currentStatus;
    }, [live?.status, stationId, live?.energyKWh, live?.meter_value, live?.ts, debugMode]);

    // Authoritative fallback: the session-level backend status.
    //
    // The simulator / virtual chargers ("test" station and friends) do
    // not physically unplug, so no StatusNotification → Available is
    // ever emitted, and the user was left staring at "Unplug your
    // vehicle" indefinitely with the session already finalized on the
    // server. Poll the customer-session read model at a bounded rate
    // and advance as soon as the backend confirms finalization —
    // same signal set ChargeMonitoring's Part 6 already trusts.
    //
    // Payment capture and receipt finalization are unaffected: they
    // are triggered by the stop-transaction flow in the backend and by
    // SUMMARY / detail pages downstream, both of which we're advancing
    // toward — not skipping.
    useEffect(() => {
        if (!sessionUid) return;
        if (debugMode) return;
        if (hasNavigatedRef.current) return;

        let cancelled = false;
        let attempts = 0;
        let intervalId = null;

        const stopPolling = () => {
            if (intervalId != null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const attempt = async () => {
            if (cancelled || hasNavigatedRef.current) {
                stopPolling();
                return;
            }
            attempts += 1;
            try {
                const result = await getCustomerSession(sessionUid);
                if (cancelled || hasNavigatedRef.current) return;

                if (result?.ok && isBackendFinalized(result.session)) {
                    console.log('[DisconnectCharger] backend finalized — advancing to summary', {
                        sessionUid,
                        status: result.session?.status,
                        finalized_at: result.session?.finalized_at,
                    });
                    hasNavigatedRef.current = true;
                    stopPolling();
                    clearActiveSession();
                    // Use the same forward transition the connector-
                    // Available fast path uses. DISCONNECT_CHARGER's next
                    // step is SUMMARY per STEP_FORWARD (7 → 8), so this
                    // deposits the user on the summary just like a
                    // physical unplug would.
                    handleIncreaseStepRef.current();
                    return;
                }

                if (attempts >= FINALIZATION_POLL_MAX_ATTEMPTS) {
                    console.warn('[DisconnectCharger] backend finalization timeout — surfacing fallback state');
                    stopPolling();
                    if (!cancelled) setFallbackVisible(true);
                }
            } catch (err) {
                console.warn('[DisconnectCharger] finalization poll error:', err?.message);
                if (attempts >= FINALIZATION_POLL_MAX_ATTEMPTS) {
                    stopPolling();
                    if (!cancelled) setFallbackVisible(true);
                }
            }
        };

        // Fire immediately, then at a bounded cadence.
        attempt();
        intervalId = setInterval(attempt, FINALIZATION_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [sessionUid, debugMode, pollGeneration]);

    // Force End is offered from this screen only when the finalization
    // poll has timed out — i.e. we told the charger to stop, gave it
    // 30 seconds to confirm, and it still has not. This is the
    // customer's billing-recovery escape hatch. We ONLY show it after
    // the timeout so a healthy stop flow (charger confirms within
    // seconds) never surfaces it, matching the spec's "reveal Force
    // End after the existing reasonable delay" requirement.
    //
    // On success we do NOT navigate away — the poll above continues,
    // and it advances to SUMMARY only when the backend confirms the
    // row is terminal. This preserves the "never render Session
    // Summary until backend confirms" invariant even when the
    // customer explicitly forced billing closure.
    const [forceEndPending, setForceEndPending] = useState(false);
    const [forceEndMessage, setForceEndMessage] = useState(null);

    const handleForceEnd = async () => {
        if (forceEndPending) return;
        if (!sessionUid) return;
        setForceEndPending(true);
        setForceEndMessage(null);
        let result;
        try {
            result = await forceEndSession(sessionUid);
        } catch (err) {
            result = { ok: false, error: 'INTERNAL_ERROR', message: err?.message };
        }
        setForceEndPending(false);
        if (result?.ok) {
            setForceEndMessage({
                kind: 'ok',
                text: 'We finalized your billing using the last available charger data. Please unplug the vehicle if you have not already.',
            });
            // Restart the terminal-status poll so the confirmed advance
            // can fire. The 30 s fallback already ran `stopPolling()`,
            // so without this the customer would sit on the fallback
            // screen forever even after a successful force end. We
            // hide the fallback UI while the fresh poll runs; the
            // outer effect re-arms on `pollGeneration` changing.
            setFallbackVisible(false);
            setPollGeneration((n) => n + 1);
        } else {
            const t = translateStopError(result);
            setForceEndMessage({ kind: 'error', text: `${t.title}. ${t.message}` });
        }
    };

    return (
        <StatusScreen>
            <StatusView
                status={STATUS.UNPLUG}
            />
            {fallbackVisible && (
                <div
                    className="disconnect-charger-fallback"
                    role="status"
                    aria-live="polite"
                >
                    <p>Still waiting for the charger to confirm…</p>
                    <p className="disconnect-charger-fallback__hint">
                        Your charger has not yet confirmed the stop. You can end the billing
                        session now — the charger may still need to be unplugged manually.
                    </p>
                    <button
                        type="button"
                        className="disconnect-charger-fallback__force-end"
                        onClick={handleForceEnd}
                        disabled={forceEndPending}
                        aria-busy={forceEndPending}
                    >
                        {forceEndPending ? 'Ending billing session…' : 'End billing session'}
                    </button>
                    {forceEndMessage && (
                        <p
                            className={
                                forceEndMessage.kind === 'ok'
                                    ? 'disconnect-charger-fallback__ok'
                                    : 'disconnect-charger-fallback__error'
                            }
                            role="status"
                            aria-live="polite"
                        >
                            {forceEndMessage.text}
                        </p>
                    )}
                </div>
            )}
        </StatusScreen>
    );
};

export default DisconnectCharger;
